import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { debounce, partition, uniq } from "es-toolkit";
import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";
import { useOnClickOutside } from "usehooks-ts";

import { getApiErrorMessage, teamSearch, userSearch } from "@/api";
import { FormFieldError } from "@/components/form/FormField";
import { FormInput } from "@/components/form/FormInput";
import { Icon } from "@/components/icons/Icon";
import { TeamListItem } from "@/components/team/TeamListItem";
import { twMerge } from "@/utils/twMerge";
import { validateEmail } from "@/utils/validateEmail";

import { UserListItem } from "./UserListItem";

import type { TeamShort, UserShort } from "@/api";

const ResultWrapper = ({
  children,
  label,
  onClick,
}: {
  children: React.ReactNode;
  label: string;
  onClick?: () => void;
}) => {
  if (onClick) {
    return (
      <button
        aria-label={label}
        onClick={onClick}
        type="button"
        className={twMerge(
          "group",
          "flex flex-1 flex-row items-center w-full rounded-12 p-2 pl-1.25 text-left",
          "hover:bg-blue-100 hover:dark:bg-blue-900",
          "sm:hover:!bg-transparent",
        )}
      >
        {children}
      </button>
    );
  }

  return (
    <div
      aria-label={label}
      className={twMerge(
        "group",
        "flex flex-1 flex-row items-center w-full rounded-12 p-2 pl-1.25 text-left",
        "hover:bg-blue-100 hover:dark:bg-blue-900",
        "sm:hover:!bg-transparent",
      )}
    >
      {children}
    </div>
  );
};

export const Email = ({ email }: { email: string }) => {
  return (
    <div className={twMerge("flex flex-row items-center min-w-0 mr-auto")}>
      <div className={twMerge("flex w-8 h-8", "sm:w-10 sm:h-10")}>
        <Icon name="Email" className="m-auto" />
      </div>
      <div
        className={twMerge(
          "relative flex flex-1 flex-col min-w-0 ml-1.5 my-auto",
          "leading-[1.2] tracking-tight",
        )}
      >
        <div
          className={twMerge("flex-1 truncate -mt-0.5 text-[15px] font-normal", "sm:text-[17px]")}
        >
          {email}
        </div>
      </div>
    </div>
  );
};

const StatusInvite = ({ onClick, className }: { onClick?: () => void; className?: string }) => {
  const t = useTranslations();

  if (onClick) {
    return (
      <button
        type="button"
        aria-label={t("teamDialog.invite")}
        onClick={onClick}
        className={twMerge(
          "sm:hidden opacity-0 group-hover:opacity-100",
          "flex items-center justify-center rounded-6 h-8.5 px-2 bg-white dark:bg-black",
          "text-[15px] tracking-tight text-blue-500",
          className,
        )}
      >
        {t("teamDialog.invite")}
      </button>
    );
  }

  return (
    <div
      className={twMerge(
        "sm:hidden opacity-0 group-hover:opacity-100",
        "flex items-center justify-center rounded-6 h-8.5 px-2 bg-white dark:bg-black",
        "text-[15px] tracking-tight text-blue-500",
        className,
      )}
    >
      {t("teamDialog.invite")}
    </div>
  );
};

// TODO: use ListItem component for email. not ResultWrapper.

const parseEmails = (input: string) => {
  const parts = input
    .split(/[,;\n]/)
    .map((part) => part.trim())
    .filter(Boolean);
  const [emails, invalidParts] = partition(parts, validateEmail);
  return { emails: uniq(emails), invalidParts: uniq(invalidParts) };
};

export type UserSearchInputProps = {
  initialValue?: string;
  className?: string;
  placeholder?: string;
  allowMultipleEmails?: boolean;
  submitAllLabel?: (count: number) => string;
  onReset?: () => void;
  onInputChange?: () => void;
  onEmailClick?: (email: string) => Promise<unknown> | void;
} & (
  | {
      searchMode: "user";
      onUserClick: (user: UserShort) => Promise<unknown> | void;
      onTeamClick?: never;
    }
  | {
      searchMode: "team";
      onTeamClick: (team: TeamShort) => Promise<unknown> | void;
      onUserClick?: never;
    }
  | {
      searchMode?: "user";
      onUserClick: (user: UserShort) => Promise<unknown> | void;
      onTeamClick?: never;
    }
);

export const UserSearchInput = observer(function UserSearchInput({
  initialValue,
  className,
  placeholder,
  allowMultipleEmails,
  submitAllLabel,
  onReset,
  onInputChange,
  onUserClick,
  onTeamClick,
  onEmailClick,
  searchMode = "user",
}: UserSearchInputProps) {
  const t = useTranslations();
  const invitesInputRef = useRef<HTMLDivElement>(null!);
  const inputRef = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState("");
  const [specificUserError, setSpecificUserError] = useState<{
    username: string;
    error: string;
  } | null>(null);
  const [specificTeamError, setSpecificTeamError] = useState<{
    permalink: string;
    error: string;
  } | null>(null);
  const [serverError, setServerError] = useState("");
  const [showResults, setShowResults] = useState(false);
  const [status, setStatus] = useState<"idle" | "loading" | "loaded" | "error">("idle");
  const [parsedEmails, setParsedEmails] = useState<string[]>([]);
  const [invalidEmailParts, setInvalidEmailParts] = useState<string[]>([]);
  const [emailErrors, setEmailErrors] = useState<Record<string, string>>({});
  const [users, setUsers] = useState<UserShort[]>([]);
  const [teams, setTeams] = useState<TeamShort[]>([]);

  useOnClickOutside(invitesInputRef, () => {
    setShowResults(false);
    setServerError("");
    setSpecificUserError(null);
    setSpecificTeamError(null);
  });

  const reset = () => {
    setValue("");
    setServerError("");
    setSpecificUserError(null);
    setSpecificTeamError(null);
    setShowResults(false);
    setParsedEmails([]);
    setInvalidEmailParts([]);
    setEmailErrors({});
    setStatus("idle");
    setUsers([]);
    setTeams([]);
    onReset?.();
  };

  const fetchUsers = useCallback(async (query: string) => {
    if (query.length < 3) {
      return;
    }

    try {
      const { users: matchedUsers } = await userSearch({ query });

      if (matchedUsers) {
        setUsers(matchedUsers);
      }
      setStatus("loaded");
    } catch (error) {
      setServerError(getApiErrorMessage(error));
      setStatus("error");
    }
  }, []);

  const fetchTeams = useCallback(async (query: string) => {
    if (query.length < 3) {
      return;
    }

    try {
      const { teams: matchedTeams } = await teamSearch({ query });

      if (matchedTeams) {
        setTeams(matchedTeams);
      }
      setStatus("loaded");
    } catch (error) {
      setServerError(getApiErrorMessage(error));
      setStatus("error");
    }
  }, []);

  const debouncedFetchUsers = useMemo(() => {
    return debounce(fetchUsers, 500);
  }, [fetchUsers]);

  const debouncedFetchTeams = useMemo(() => {
    return debounce(fetchTeams, 500);
  }, [fetchTeams]);

  const resolveEmails = useCallback(
    (newValue: string) => {
      if (!onEmailClick) {
        return null;
      }
      if (allowMultipleEmails) {
        const { emails, invalidParts } = parseEmails(newValue);
        return emails.length > 0 ? { emails, invalidParts } : null;
      }
      return validateEmail(newValue) ? { emails: [newValue], invalidParts: [] } : null;
    },
    [onEmailClick, allowMultipleEmails],
  );

  const resolveOrSearch = useCallback(
    (newValue: string) => {
      const resolved = resolveEmails(newValue);
      setParsedEmails(resolved?.emails ?? []);
      setInvalidEmailParts(resolved?.invalidParts ?? []);
      if (resolved) {
        return;
      }
      if (searchMode === "team") {
        debouncedFetchTeams(newValue);
      } else {
        debouncedFetchUsers(newValue);
      }
    },
    [resolveEmails, searchMode, debouncedFetchTeams, debouncedFetchUsers],
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setValue(newValue);
    setServerError("");
    setSpecificUserError(null);
    setSpecificTeamError(null);
    setEmailErrors({});
    setUsers([]);
    setTeams([]);
    onInputChange?.();
    setStatus("loading");
    setShowResults(true);
    resolveOrSearch(newValue);
  };

  useEffect(() => {
    if (initialValue) {
      setValue(initialValue);
      resolveOrSearch(initialValue);
    }
  }, [initialValue, resolveOrSearch]);

  const handleInputFocus = () => {
    setShowResults(true);
  };

  const handleClearClick = () => {
    inputRef.current?.focus();
    reset();
  };

  const submitEmails = async (emails: string[]) => {
    const results = await Promise.allSettled(emails.map(async (email) => onEmailClick?.(email)));
    const invited: string[] = [];
    const failures: Record<string, string> = {};
    results.forEach((result, index) => {
      if (result.status === "fulfilled") {
        invited.push(emails[index]);
      } else {
        failures[emails[index]] = getApiErrorMessage(result.reason);
      }
    });

    setEmailErrors((prev) => {
      const next = { ...prev, ...failures };
      invited.forEach((email) => delete next[email]);
      return next;
    });
    if (invited.length === 0) {
      return;
    }
    const remaining = parsedEmails.filter((email) => !invited.includes(email));
    if (remaining.length === 0 && invalidEmailParts.length === 0) {
      reset();
      return;
    }
    setParsedEmails(remaining);
    setValue([...remaining, ...invalidEmailParts].join(", "));
  };

  const handleEmailClick = (email: string) => submitEmails([email]);
  const handleSubmitAllClick = () => submitEmails(parsedEmails);

  const handleUserClick = async (user: UserShort) => {
    setSpecificUserError(null);
    try {
      await onUserClick?.(user);
      reset();
    } catch (error) {
      setSpecificUserError({ username: user.username, error: getApiErrorMessage(error) });
    }
  };

  const handleTeamClick = async (team: TeamShort) => {
    setSpecificTeamError(null);
    try {
      await onTeamClick?.(team);
      reset();
    } catch (error) {
      setSpecificTeamError({
        permalink: team.permalink,
        error: getApiErrorMessage(error),
      });
    }
  };

  const renderEmails = () => {
    return (
      <>
        {parsedEmails.map((email) => (
          <Fragment key={email}>
            <ResultWrapper label="parsed-email" onClick={() => handleEmailClick(email)}>
              <Email email={email} />
              <StatusInvite />
            </ResultWrapper>
            {emailErrors[email] && (
              <FormFieldError className="text-center" message={emailErrors[email]} />
            )}
          </Fragment>
        ))}
        {submitAllLabel && parsedEmails.length > 1 && (
          <button
            type="button"
            onClick={handleSubmitAllClick}
            className={twMerge(
              "flex items-center justify-center w-full rounded-12 p-2",
              "text-[15px] tracking-tight text-blue-500 font-medium",
              "hover:bg-blue-100 hover:dark:bg-blue-900",
            )}
          >
            {submitAllLabel(parsedEmails.length)}
          </button>
        )}
      </>
    );
  };

  const renderUser = (user: UserShort) => {
    return (
      <Fragment key={user.username}>
        <UserListItem
          user={user}
          size="sm"
          invertedHoverColor
          rightController={<StatusInvite onClick={() => handleUserClick(user)} />}
          label="matched-user"
          onClick={() => handleUserClick(user)}
        />
        {specificUserError?.username === user.username && (
          <FormFieldError className="text-center" message={specificUserError.error} />
        )}
      </Fragment>
    );
  };

  const renderTeam = (team: TeamShort) => {
    return (
      <Fragment key={team.permalink}>
        <TeamListItem
          team={team}
          size="sm"
          invertedHoverColor
          rightController={<StatusInvite onClick={() => handleTeamClick(team)} />}
          label="matched-team"
          onClick={() => handleTeamClick(team)}
        />
        {specificTeamError?.permalink === team.permalink && (
          <FormFieldError className="text-center" message={specificTeamError.error} />
        )}
      </Fragment>
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // if only one result is found or parsed email is in the input value, press enter to invite
    if (e.key === "Enter") {
      if (searchMode === "user" && users.length === 1) {
        handleUserClick(users[0]);
      }
      if (searchMode === "team" && teams.length === 1) {
        handleTeamClick(teams[0]);
      }
      if (parsedEmails.length > 0) {
        handleSubmitAllClick();
      }
    }
  };

  const renderPanel = () => {
    if (serverError) {
      return <FormFieldError message={serverError} />;
    }
    if (parsedEmails.length > 0 || invalidEmailParts.length > 0) {
      return (
        <>
          {renderEmails()}
          {invalidEmailParts.length > 0 && (
            <FormFieldError
              className="text-center"
              message={t("teamDialog.invalidEmails", { emails: invalidEmailParts.join(", ") })}
            />
          )}
        </>
      );
    }
    if (searchMode === "user" && users.length > 0) {
      return users.map(renderUser);
    }
    if (searchMode === "team" && teams.length > 0) {
      return teams.map(renderTeam);
    }
    const emptyMessage =
      searchMode === "team" ? t("teamDialog.noTeamsFound") : t("teamDialog.noUsersFound");
    return (
      <div className="flex items-center justify-center p-2 tracking-tight">
        {status === "loading" ? <Icon name="Spinner" className="animate-spin" /> : emptyMessage}
      </div>
    );
  };

  return (
    <div
      ref={invitesInputRef}
      data-testid="invites-input"
      className={twMerge("relative z-10", className)}
    >
      <FormInput
        ref={inputRef}
        placeholder={placeholder || t("teamDialog.inviteBy")}
        aria-label={placeholder || t("teamDialog.inviteBy")}
        type="text"
        autoCapitalize="none"
        className="dark:bg-gray-800"
        value={value}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        onClearClick={handleClearClick}
        onKeyDown={handleKeyDown}
        keepPlaceholderOnTop={false}
        valuePostfix={
          value.length === 0 && (
            <StatusInvite
              className={twMerge(
                "mx-1.25 my-auto opacity-100 font-medium",
                "bg-blue-100 dark:bg-blue-900 text-blue-500 dark:text-blue-200",
                "hover:bg-blue-200 dark:hover:bg-blue-800",
              )}
              onClick={() => inputRef.current?.focus()}
            />
          )
        }
      />
      {showResults && value.length > 2 && (
        <div
          className={twMerge(
            "absolute top-100% left-0 w-full max-h-[180px] overflow-y-auto mt-2.5 p-2.5",
            "rounded-14 bg-block-bg shadow-md dark:shadow-block-inverted select-none",
          )}
        >
          {renderPanel()}
        </div>
      )}
    </div>
  );
});
